/**
 * New Relic timing for the ToC dialog: from the open request to the dialog's first commit.
 *
 * The first open of a page session waits for the dialog's lazy chunk; a 500-row leaderboard once kept that render from
 * ever committing (an endless loader). `TocDialogOpened` reports how long each open took, `TocDialogSlowOpen` an open
 * still waiting after `SLOW_OPEN_MS` — the stuck case, which never reaches `TocDialogOpened` unless something unsticks it.
 */

export const SLOW_OPEN_MS = 10_000;

type PageActionAttributes = Record<string, string | number | boolean>;

let pending: { startedAt: number; isFirstOpen: boolean; attributes: PageActionAttributes; slowTimer: number } | null =
  null;
let hasOpenedThisSession = false;

function addPageAction(name: string, attributes: PageActionAttributes) {
  window.newrelic?.addPageAction(name, attributes);
}

/** The ToC dialog was asked to open; a request already waiting keeps its start. */
export function markTocDialogOpenRequested(attributes: PageActionAttributes) {
  if (pending) return;
  const startedAt = performance.now();
  const isFirstOpen = !hasOpenedThisSession;
  const slowTimer = window.setTimeout(() => {
    addPageAction('TocDialogSlowOpen', {
      ...attributes,
      isFirstOpen,
      elapsedMs: Math.round(performance.now() - startedAt),
      visibilityState: document.visibilityState,
    });
  }, SLOW_OPEN_MS);
  pending = { startedAt, isFirstOpen, attributes, slowTimer };
}

/** The ToC dialog committed: reports the open's duration. */
export function markTocDialogOpened() {
  if (!pending) return;
  const { startedAt, isFirstOpen, attributes, slowTimer } = pending;
  window.clearTimeout(slowTimer);
  pending = null;
  hasOpenedThisSession = true;
  addPageAction('TocDialogOpened', {
    ...attributes,
    isFirstOpen,
    durationMs: Math.round(performance.now() - startedAt),
    // Hidden: the open only finished once the player left the tab.
    visibilityState: document.visibilityState,
  });
}

/** The dialog closed before it committed (the player gave up): nothing to report as opened. */
export function cancelTocDialogOpen() {
  if (!pending) return;
  window.clearTimeout(pending.slowTimer);
  pending = null;
}

export function resetTocDialogOpenTimingForTests() {
  cancelTocDialogOpen();
  hasOpenedThisSession = false;
}
